import React from "react";
import { Modal, Typography, Divider, Tag } from "antd";
import {
  CalendarOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  LinkOutlined,
  SettingOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./ReservationInfoModal.scss";

const { Title, Text, Paragraph } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
};

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  color: string;
  children: React.ReactNode;
}> = ({ icon, title, color, children }) => (
  <div className="rim-section">
    <div className="rim-section__header">
      <span className="rim-section__icon" style={{ color, background: `${color}18` }}>
        {icon}
      </span>
      <Title level={5} className="rim-section__title">{title}</Title>
    </div>
    <div className="rim-section__body">{children}</div>
  </div>
);

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rim-bullet">
    <span className="rim-bullet__dot" />
    <span>{children}</span>
  </div>
);

const ReservationInfoModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={
        <div className="rim-title">
          <span>🍽️</span>
          <span>{t("How Reservations Work")}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      className="reservation-info-modal standardHeader"
    >
      <div className="rim-body">

        {/* Views */}
        <Section icon={<CalendarOutlined />} title={t("Views")} color="#6366f1">
          <Bullet>
            <strong>{t("Day view")}</strong> — {t("Default. Shows all reservations for the selected day grouped by hour. Best for daily operations during service.")}
          </Bullet>
          <Bullet>
            <strong>{t("Week view")}</strong> — {t("7-column grid. Shows reservation count per day. Click any day to jump to Day view.")}
          </Bullet>
          <Bullet>
            <strong>{t("Month view")}</strong> — {t("Calendar grid with dots per status. Click a day to drill in.")}
          </Bullet>
        </Section>

        <Divider className="rim-divider" />

        {/* Adding reservations */}
        <Section icon={<PlusOutlined />} title={t("Adding Reservations")} color="#10b981">
          <Bullet>
            {t("Click")} <strong>{t("New Reservation")}</strong> {t("in the top right to open the form.")}
          </Bullet>
          <Bullet>
            {t("Required fields: guest name, phone, date, time, number of guests.")}
          </Bullet>
          <Bullet>
            {t("If tables are configured, choose one — taken tables are marked and disabled.")}
          </Bullet>
          <Bullet>
            {t("Source tracks where the reservation came from: Internal, Phone, Website, or Widget.")}
          </Bullet>
        </Section>

        <Divider className="rim-divider" />

        {/* Status flow */}
        <Section icon={<CheckCircleOutlined />} title={t("Reservation Status Flow")} color="#f59e0b">
          <div className="rim-status-flow">
            <Tag color="orange">{t("Pending")}</Tag>
            <span className="rim-arrow">→</span>
            <Tag color="green">{t("Confirmed")}</Tag>
            <span className="rim-arrow">→</span>
            <Tag color="blue">{t("Seated")}</Tag>
            <span className="rim-arrow">→</span>
            <Tag color="default">{t("Completed")}</Tag>
          </div>
          <div style={{ marginTop: 8 }}>
            <Bullet><Tag color="red">{t("Cancelled")}</Tag> — {t("Guest or staff cancelled.")}</Bullet>
            <Bullet><Tag color="default">{t("No Show")}</Tag> — {t("Guest did not arrive.")}</Bullet>
          </div>
        </Section>

        <Divider className="rim-divider" />

        {/* Quick actions */}
        <Section icon={<ThunderboltOutlined />} title={t("Quick Actions")} color="#3b82f6">
          <Bullet>
            {t("Each card shows one-tap buttons: Confirm, Seated, Cancel — no need to open the full detail.")}
          </Bullet>
          <Bullet>
            {t("Click anywhere on a card to open the details drawer for full info and status change.")}
          </Bullet>
          <Bullet>
            {t("Use the pencil icon to edit or trash to delete directly from the detail drawer.")}
          </Bullet>
        </Section>

        <Divider className="rim-divider" />

        {/* Tables */}
        <Section icon={<TeamOutlined />} title={t("Table Management")} color="#8b5cf6">
          <Bullet>
            {t("Configure your tables in")} <strong>{t("Settings")}</strong> — {t("set a name and seat count for each.")}
          </Bullet>
          <Bullet>
            {t("When adding a reservation, the table selector shows which tables are already taken at the chosen date & time.")}
          </Bullet>
          <Bullet>
            {t("The backend prevents double-booking: two reservations cannot share the same table at the same time.")}
          </Bullet>
          <Bullet>
            {t("Public bookings auto-assign a free table with enough capacity.")}
          </Bullet>
        </Section>

        <Divider className="rim-divider" />

        {/* Public booking */}
        <Section icon={<GlobalOutlined />} title={t("Public Booking — Guest Integration")} color="#ff6b35">
          <Bullet>
            {t("Click")} <strong>{t("Public Link")}</strong> {t("to copy a direct booking URL for guests.")}
          </Bullet>
          <Bullet>
            <strong>iFrame embed</strong> — {t("paste one")} <code>{"<iframe>"}</code> {t("tag on any website.")}
          </Bullet>
          <Bullet>
            <strong>Widget script</strong> — {t("one")} <code>{"<script>"}</code> {t("tag adds a floating 'Reserve a Table' button.")}
          </Bullet>
          <Bullet>
            <strong>API</strong> — {t("developers can POST directly to")} <code>/api/public/reservations</code>.
          </Bullet>
          <Bullet>
            {t("If")} <strong>{t("Require confirmation")}</strong> {t("is ON in settings, public bookings arrive as Pending and must be confirmed by staff.")}
          </Bullet>
        </Section>

        <Divider className="rim-divider" />

        {/* Settings */}
        <Section icon={<SettingOutlined />} title={t("Settings")} color="#6b7280">
          <Bullet>{t("Restaurant name — shown on the public booking page.")}</Bullet>
          <Bullet>{t("Opening / closing time — defines available time slots.")}</Bullet>
          <Bullet>{t("Slot duration — 15, 30, 45, 60 min intervals.")}</Bullet>
          <Bullet>{t("Min notice — how many hours before a slot guests can still book.")}</Bullet>
          <Bullet>{t("Advance booking — how many days ahead guests can reserve.")}</Bullet>
          <Bullet>{t("Require confirmation — public bookings arrive as Pending instead of Confirmed.")}</Bullet>
        </Section>

      </div>
    </Modal>
  );
};

export default ReservationInfoModal;
